import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import Modal from "./Modal";
import LoadingSpinner from "./LoadingSpinner";
import { KnowBe4SyncStatus, KnowBe4TestResult } from "./KnowBe4Diagnostics";
import { formatUtcDateTime, parseUtcDate } from "../utils/dateTime";

/**
 * Super Admin page: KnowBe4 diagnostics for one organization (GET / POST / PUT
 * /SuperAdmin/organizations/{id}/knowbe4[/test-connection | /diagnostic-logging]). A SuperAdmin has no
 * implicit org role and so cannot reach the KnowBe4 card in a customer org's Integration Settings;
 * this panel shows the same last-sync banner, test result and detailed-logging switch. The server
 * decides what the caller may do: status.canRunDiagnostics (CanRunKnowBe4Diagnostics, a listed
 * operator) shows Test Connection; status.canToggleLogging (CanToggleIntegrationDiagnosticLogging)
 * shows the logging switch.
 */
const SuperAdminKnowBe4Panel = ({ org, onClose }) => {
  const apiCall = useApi();
  const orgId = org?.organizationId;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [loggingSaving, setLoggingSaving] = useState(false);

  useEffect(() => {
    if (orgId == null) return undefined;
    let cancelled = false;
    setStatus(null); setError(null); setLoading(true);
    apiCall("GET", `/SuperAdmin/organizations/${orgId}/knowbe4`)
      .then((data) => { if (!cancelled) setStatus(data); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || "Failed to load KnowBe4 status."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiCall, orgId]);

  const handleTest = useCallback(async () => {
    setTesting(true); setTestResult(null);
    try {
      const result = await apiCall("POST", `/SuperAdmin/organizations/${orgId}/knowbe4/test-connection`);
      setTestResult(result || { error: true });
    } catch { setTestResult({ error: true }); }
    finally { setTesting(false); }
  }, [apiCall, orgId]);

  const handleLogging = useCallback(async (enabled) => {
    setLoggingSaving(true); setError(null);
    try {
      const updated = await apiCall("PUT", `/SuperAdmin/organizations/${orgId}/knowbe4/diagnostic-logging`, { enabled });
      if (updated) setStatus(updated);
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't change detailed logging. Try again.");
    } finally { setLoggingSaving(false); }
  }, [apiCall, orgId]);

  const loggingUntil = parseUtcDate(status?.diagnosticLoggingUntil);
  const loggingOn = !!loggingUntil && loggingUntil.getTime() > Date.now();

  return (
    <Modal isOpen={!!org} onClose={onClose} title={`KnowBe4 diagnostics - ${org?.organizationName || ""}`} size="md">
      <div data-testid="superadmin-knowbe4-panel">
        {error && (
          <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm">
            {error}
          </div>
        )}
        {loading && <LoadingSpinner />}
        {!loading && status && !status.configured && (
          <p className="text-sm text-content-muted">KnowBe4 is not configured for this organization.</p>
        )}
        {!loading && status?.configured && (
          <div className="space-y-3">
            <p className="text-sm text-content-secondary">
              KnowBe4 is configured: region <strong>{status.region || "us"}</strong>
              {status.enabled ? ", enabled." : ", disabled (no sync runs)."}
            </p>
            {status.enabled && (
              <KnowBe4SyncStatus region={status.region} lastSyncAt={status.lastSyncAt} syncState={status.syncState} />
            )}
            {status.canRunDiagnostics && (
              <div className="space-y-2">
                <button onClick={handleTest} disabled={testing}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors">
                  {testing ? "Testing…" : "Test Connection"}
                </button>
                {testResult && <KnowBe4TestResult result={testResult} />}
              </div>
            )}
          </div>
        )}
        {!loading && status?.canToggleLogging && (status.configured || status.diagnosticLoggingUntil) && (
          <div data-testid="knowbe4-diagnostic-logging" className="mt-3 pt-3 border-t border-border space-y-1">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold text-content-secondary">
                Detailed logging (support): {loggingOn ? `On until ${formatUtcDateTime(status.diagnosticLoggingUntil)}` : "Off"}
              </span>
              <button onClick={() => handleLogging(!loggingOn)} disabled={loggingSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors">
                {loggingOn ? "Turn off" : "Turn on for 24 hours"}
              </button>
            </div>
            <p className="text-xs text-content-muted">Logs KnowBe4 responses with names and emails masked, for troubleshooting.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SuperAdminKnowBe4Panel;
